import { DefaultGameState, GameState } from "@shared/types";
import { css } from "../helpers/webComponents";
import { GameRouteService } from "../services/GameRouteService";

/** CSS for the Map {@link TextComponent}**/
const styles: string = css`
:host {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background: rgba(0, 0, 0, 0.75);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 9999;
}

h2 {
    color: white;
    font-weight: bold;
    margin-right: 65px
}

.map {
    padding: 20px;
    width: 1000px;
    height: 600px;
    border-radius: 10px;
    text-align: center;
    color: black;
}

.closeButton {
    background-color: lightgrey;
    padding: 10px 20px;
    margin: 0 70px 0 0;
    border-radius: 5px, white;
    text-transform: uppercase;
    cursor: pointer;
    display: inline-block;
    user-select: none;
    color: black;
    font-size: 15px;
}

.closeButton:hover {
    background-color: black;
    color: white;
}
`;

export class MapComponent extends HTMLElement {
    /** Instance of the game route service */
    private readonly _gameRouteService: GameRouteService = new GameRouteService();
    /** Current game state */
    private _currentGameState: DefaultGameState | undefined;
    /**
     * The "constructor" of a Web Component
     */
    public connectedCallback(): void {
        this.attachShadow({ mode: "open" });
        void this.refreshGameState();
        this.render();
        this.addCloseButtonListener();
    }

    /**
     * Refresh the current game state
     */
    private async refreshGameState(): Promise<void> {
        const state: GameState = await this._gameRouteService.getGameState();

        this.updateGameState(state);
    }

    /**
     * Update map based on game state
     */
    private updateGameState(state: GameState): void {
        if (state.type === "switch-page") {
            return;
        }

        this._currentGameState = state;

        this.render();
    }

    /**
     * Methode to see if rooms are visited
     */
    private isVisible(alias: string): boolean {
        const visitedRoom: string[] | undefined = this._currentGameState?.visitedRooms || [];
        return visitedRoom.includes(alias);
    }

    /**
     * Methode to hide or show rooms based on info in isVisible methode
     * @param alias Alias of the room
     * @returns Hidden or visible room on map
     */
    private getVisibility(alias: string): string {
        return this.isVisible(alias) ? "visible" : "hidden";
    }

    /**
     * Shows lines if room is visited where alias is connected
     * @param alias Alias of room
     * @returns lines show on map if room is visited where lines are connected with
     */
    private getStrokeColor(alias: string): string {
        const currentRoom: string | undefined = this._currentGameState?.roomAlias || "";
        return alias === currentRoom ? "red" : "white";
    }

    /**
     * Closes the map
     */
    private addCloseButtonListener(): void {
        const closeButton: HTMLElement | null = this.shadowRoot?.querySelector("#closeButton") as HTMLElement;
        closeButton.addEventListener("click", () => {
            this.remove();
        });
    }

    /**
     * Render the map UI
     */
    private render(): void {
        if (!this.shadowRoot) {
            return;
        }

        this.shadowRoot.innerHTML = `
            <style>${styles}</style>
            <div class="map">
              <h2>Kaart</h2>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 500 600" width="1000" height="500">
                  <!-- Verhoorkamer -->
                  <rect x="-210" y="25" width="150" height="150" fill="darkgrey" stroke="${this.getStrokeColor("interrogation")}" stroke-width="4" style="visibility: ${this.getVisibility("interrogation")}" id="interrogation"/>
                  <text x="-135" y="90" text-anchor="middle" font-size="20" font-weight="bold" fill="black" style="visibility: ${this.getVisibility("interrogation")}">Verhoor</text>
                  <text x="-135" y="125" text-anchor="middle" font-size="20" font-weight="bold" fill="black" style="visibility: ${this.getVisibility("interrogation")}">kamer</text>

                  <!-- Politiebureau -->
                  <rect x="-25" y="25" width="150" height="150" fill="darkgrey" stroke="${this.getStrokeColor("policestation")}" stroke-width="4" style="visibility: ${this.getVisibility("policestation")}" id="policestation"/>
                  <text x="50" y="90" text-anchor="middle" font-size="20" font-weight="bold" fill="black" style="visibility: ${this.getVisibility("policestation")}">Politie</text>
                  <text x="50" y="125" text-anchor="middle" font-size="20" font-weight="bold" fill="black" style="visibility: ${this.getVisibility("policestation")}">bureau</text>

                  <!-- Kelder A -->
                  <rect x="-25" y="370" width="150" height="150" fill="darkgrey" stroke="${this.getStrokeColor("basementA")}" stroke-width="4" style="visibility: ${this.getVisibility("basementA")}" id="basementA"/>
                  <text x="50" y="455" text-anchor="middle" font-size="20" font-weight="bold" fill="black" style="visibility: ${this.getVisibility("basementA")}">Kelder A</text>

                  <!-- Straat -->
                  <rect x="130" y="200" width="150" height="150" fill="darkgrey" stroke="${this.getStrokeColor("street")}" stroke-width="4" style="visibility: ${this.getVisibility("street")}" id="street"/>
                  <text x="205" y="280" text-anchor="middle" font-size="20" font-weight="bold" fill="black" style="visibility: ${this.getVisibility("street")}">Straat</text>

                  <!-- Kelder B -->
                  <rect x="-210" y="370" width="150" height="150" fill="darkgrey" stroke="${this.getStrokeColor("basementB")}" stroke-width="4" style="visibility: ${this.getVisibility("basementB")}" id="basementB"/>
                  <text x="-140" y="455" text-anchor="middle" font-size="20" font-weight="bold" fill="black" style="visibility: ${this.getVisibility("basementB")}">Kelder B</text>

                  <!-- Kantoor Zakenman -->
                  <rect x="290" y="370" width="150" height="150" fill="darkgrey" stroke="${this.getStrokeColor("office")}" stroke-width="4" style="visibility: ${this.getVisibility("office")}" id="office"/>
                  <text x="365" y="435" text-anchor="middle" font-size="20" font-weight="bold" fill="black" style="visibility: ${this.getVisibility("office")}">Kantoor</text>
                  <text x="365" y="465" text-anchor="middle" font-size="20" font-weight="bold" fill="black" style="visibility: ${this.getVisibility("office")}">Zakenman</text>

                  <!-- Woonkamer Zakenman -->
                  <rect x="475" y="370" width="150" height="150" fill="darkgrey" stroke="${this.getStrokeColor("livingroom")}" stroke-width="4" style="visibility: ${this.getVisibility("livingroom")}" id="livingroom"/>
                  <text x="550" y="435" text-anchor="middle" font-size="20" font-weight="bold" fill="black" style="visibility: ${this.getVisibility("livingroom")}">Woonkamer</text>
                  <text x="550" y="465" text-anchor="middle" font-size="20" font-weight="bold" fill="black" style="visibility: ${this.getVisibility("livingroom")}">Zakenman</text>

                  <!-- Huis Poppy -->
                  <rect x="320" y="200" width="150" height="150" fill="darkgrey" stroke="${this.getStrokeColor("housePoppyRoom")}" stroke-width="4" style="visibility: ${this.getVisibility("housePoppyRoom")}" id="housePoppyRoom"/>
                  <text x="395" y="280" text-anchor="middle" font-size="20" font-weight="bold" fill="black" style="visibility: ${this.getVisibility("housePoppyRoom")}">Huis Poppy</text>

                  <!-- Nachtclub -->
                  <rect x="290" y="25" width="150" height="150" fill="darkgrey" stroke="${this.getStrokeColor("nightclub")}" stroke-width="4" style="visibility: ${this.getVisibility("nightclub")}" id="nightclub"/>
                  <text x="365" y="105" text-anchor="middle" font-size="20" font-weight="bold" fill="black" style="visibility: ${this.getVisibility("nightclub")}">Nachtclub</text>

                  <!-- Steeg -->
                  <rect x="475" y="25" width="150" height="150" fill="darkgrey" stroke="${this.getStrokeColor("alley")}" stroke-width="4" style="visibility: ${this.getVisibility("alley")}" id="alley"/>
                  <text x="550" y="105" text-anchor="middle" font-size="20" font-weight="bold" fill="black" style="visibility: ${this.getVisibility("alley")}">Steeg</text>

                  <line x1="-27" y1="100" x2="-58" y2="100" style="stroke:white;stroke-width:4; visibility: ${this.getVisibility("interrogation")}" id="line1"/>
                  <line x1="127" y1="100" x2="202" y2="100" style="stroke:white;stroke-width:4; visibility: ${this.getVisibility("street")}" id="line2"/>
                  <line x1="200" y1="99" x2="200" y2="198" style="stroke:white;stroke-width:4; visibility: ${this.getVisibility("street")}" id="line3"/>
                  <line x1="220" y1="100" x2="220" y2="198" style="stroke:white;stroke-width:4; visibility: ${this.getVisibility("nightclub")}" id="line4"/>
                  <line x1="288" y1="100" x2="218" y2="100" style="stroke:white;stroke-width:4; visibility: ${this.getVisibility("nightclub")}" id="line5"/>
                  <line x1="442" y1="100" x2="473" y2="100" style="stroke:white;stroke-width:4; visibility: ${this.getVisibility("alley")}" id="line6"/>
                  <line x1="282" y1="280" x2="318" y2="280" style="stroke:white;stroke-width:4; visibility: ${this.getVisibility("housePoppyRoom")}" id="line7"/>
                  <line x1="288" y1="450" x2="218" y2="450" style="stroke:white;stroke-width:4; visibility: ${this.getVisibility("office")}" id="line8"/>
                  <line x1="220" y1="352" x2="220" y2="452" style="stroke:white;stroke-width:4; visibility: ${this.getVisibility("office")}" id="line9"/>
                  <line x1="127" y1="450" x2="202" y2="450" style="stroke:white;stroke-width:4; visibility: ${this.getVisibility("basementA")}" id="line10"/>
                  <line x1="200" y1="352" x2="200" y2="452" style="stroke:white;stroke-width:4; visibility: ${this.getVisibility("basementA")}" id="line11"/>
                  <line x1="-27" y1="450" x2="-58" y2="450" style="stroke:white;stroke-width:4; visibility: ${this.getVisibility("basementB")}" id="line12"/>
                  <line x1="442" y1="452" x2="473" y2="452" style="stroke:white;stroke-width:4; visibility: ${this.getVisibility("livingroom")}" id="line13"/>
                </svg>
                <div class="button">
                  <button class="closeButton" id="closeButton">Kaart sluiten</button>
                </div>
            </div>  
        `;
        this.addCloseButtonListener();
    }
}
